import type { UserPosition } from "@/types/geolocation"
import { useMap } from "@vis.gl/react-google-maps"

import { setMapCenter } from "@/lib/gmap-utils"
import {
  handleGeoErrorWithToast,
  useRequestGeolocation,
} from "@/lib/hooks/use-request-geolocation"
import { useTranslation } from "@/lib/i18n/i18n-context"
import { selectDisplayPosition } from "@/lib/storage/geolocation-selectors"
import { useAppSelector } from "@/lib/storage/hooks"
import { isTouch } from "@/lib/utils/dom"
import IconLayer from "@/components/icons/layer"
import IconLocationTarget from "@/components/icons/location-target"
import IconMinus from "@/components/icons/minus"
import IconPlus from "@/components/icons/plus"
import { IconButton } from "../ui/icon-button"
import { Select, SelectContent, SelectItem, SelectTrigger } from "../ui/select"
import { Separator } from "../ui/separator"

export type MapDisplayType = "roadmap" | "satellite"

export interface MapControlsConfig {
  location?: boolean
  displayType?: boolean
  zoom?: boolean
}

const MAP_DISPLAY_TYPE_ITEMS: Record<MapDisplayType, string> = {
  roadmap: "Default",
  satellite: "Satellite",
}

interface MapControlsProps {
  padding?: google.maps.Padding
  mapDisplayType: MapDisplayType
  onMapDisplayTypeChange: (mapDisplayType: MapDisplayType) => void
  onUserInteraction?: () => void
  config?: MapControlsConfig
}

const isMapDisplayType = (value: string): value is MapDisplayType => {
  return value in MAP_DISPLAY_TYPE_ITEMS
}

export function MapControls({
  padding,
  mapDisplayType,
  onMapDisplayTypeChange,
  onUserInteraction,
  config,
}: MapControlsProps) {
  const t = useTranslation()
  const showLocation = config?.location ?? true
  const showDisplayType = config?.displayType ?? true
  const showZoom = config?.zoom ?? true

  const map = useMap()
  const currentPosition = useAppSelector(selectDisplayPosition)

  const centerOnCurrentPosition = (position: UserPosition) => {
    if (!map) return

    setMapCenter({
      map,
      center: new google.maps.LatLng(position.latitude, position.longitude),
    })
    map.setZoom(16)
  }

  const { requestLocation, isAcquiring: isAcquiringLocation } =
    useRequestGeolocation({
      onPosition: centerOnCurrentPosition,
      onError: handleGeoErrorWithToast,
    })

  const handleShowCurrentLocation = () => {
    if (!map) return

    onUserInteraction?.()

    if (currentPosition) {
      centerOnCurrentPosition(currentPosition)
      return
    }

    requestLocation()
  }

  const handleZoomIn = () => {
    if (!map) return
    const currentZoom = map.getZoom() ?? 0
    map.setZoom(currentZoom + 1)
  }

  const handleZoomOut = () => {
    if (!map) return
    const currentZoom = map.getZoom() ?? 0
    map.setZoom(currentZoom - 1)
  }

  const handleMapDisplayTypeChange = (value: string | null) => {
    if (!value || !isMapDisplayType(value)) return
    onUserInteraction?.()
    onMapDisplayTypeChange(value)
  }

  return (
    <div
      className="absolute right-0 bottom-0 mr-5 mb-5 flex flex-col gap-2.5"
      style={{
        bottom: `${padding?.bottom || 0}px`,
      }}
    >
      {showLocation && (
        <IconButton
          variant="overlay"
          size="md"
          icon={{ icon: IconLocationTarget, className: "text-[1.625em]" }}
          label={t("showYourLocation")}
          tooltip
          loading={isAcquiringLocation}
          onClick={handleShowCurrentLocation}
        />
      )}
      {showDisplayType && (
        <Select
          value={mapDisplayType}
          onValueChange={handleMapDisplayTypeChange}
          items={MAP_DISPLAY_TYPE_ITEMS}
        >
          <SelectTrigger
            render={
              <IconButton
                variant="overlay"
                size="md"
                icon={{ icon: IconLayer, className: "text-[1.625em]" }}
                label={t("changeMapDisplayType")}
              />
            }
          />
          <SelectContent
            align="center"
            side="left"
            alignItemWithTrigger={false}
          >
            {Object.entries(MAP_DISPLAY_TYPE_ITEMS).map(([value, label]) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      {showZoom && !isTouch() && (
        <div className="flex h-20 w-(--button-md-size) flex-col overflow-hidden rounded-full border border-background bg-background/90 shadow-(--button-shadow)">
          <button
            type="button"
            onClick={handleZoomIn}
            aria-label={t("zoomIn")}
            className="flex flex-1 items-center justify-center"
          >
            <IconPlus strokeWidth={2.5} />
          </button>
          <Separator />
          <button
            type="button"
            onClick={handleZoomOut}
            aria-label={t("zoomOut")}
            className="flex flex-1 items-center justify-center"
          >
            <IconMinus strokeWidth={2.5} />
          </button>
        </div>
      )}
    </div>
  )
}
